/**
 * emit-goose.ts — prepare a Claude Code JSONL transcript for `goose session import`.
 *
 * Goose (>=1.10) ships a FIRST-PARTY
 * Claude Code importer (crates/goose/src/session/import_formats/claude_code.rs):
 * `detect_format` flags ClaudeCode on any line carrying `sessionId` + (`type`|`uuid`),
 * and maps text/thinking/tool_use/tool_result 1:1 onto goose MessageContent.
 *
 * DECISION: feed the ORIGINAL autofix CC transcript straight to the importer rather
 * than reconstructing CC JSONL from a PortableSession (what the prototype's
 * emit/goose.mjs did — it only had a PortableSession fixture to work from). fork.ts
 * already holds the full, cwd-patched original transcript, so the original is both
 * strictly higher-fidelity (no capping/pruning that PortableSession applies) and
 * simpler. The only transform is guaranteeing a deterministic, resumable session
 * NAME: `goose session import` has no `--name`, and derives the name from the
 * transcript's `ai-title` line. If the transcript already has one we honor it; if
 * not, we prepend a synthetic `ai-title` line so `goose session -r --name <n>` works.
 */

import type { PortableSession } from "./extractor.js";

export interface GooseImport {
  content: string; // the JSONL to write and hand to `goose session import`
  sessionName: string; // the ai-title the importer will use as the session name
}

// Derive a stable, human-ish, collision-resistant name from the session id.
function defaultSessionName(sessionId: string): string {
  const suffix = String(sessionId).replace(/[^A-Za-z0-9]/g, "").slice(0, 12) || "session";
  return `sailfish-autofix-${suffix}`;
}

// Find the aiTitle of the first `ai-title` line already present in the transcript.
function existingAiTitle(jsonlContent: string): string | null {
  for (const line of jsonlContent.split("\n")) {
    const raw = line.trim();
    if (!raw || !raw.includes('"ai-title"')) continue;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.type === "ai-title" && typeof parsed.aiTitle === "string" && parsed.aiTitle) {
        return parsed.aiTitle;
      }
    } catch {
      // not JSON / not an ai-title record — keep scanning
    }
  }
  return null;
}

/**
 * Produce the JSONL + session name for `goose session import`. Pure: no I/O.
 */
export function prepareGooseImport(jsonlContent: string, session: PortableSession): GooseImport {
  const existing = existingAiTitle(jsonlContent);
  if (existing) {
    // Transcript already names itself — honor it so the printed resume name matches.
    return { content: jsonlContent, sessionName: existing };
  }

  const meta = session.meta || ({} as PortableSession["meta"]);
  const sessionId = meta.source_session_id || "portable-import";
  const sessionName = defaultSessionName(sessionId);

  // Prepend a synthetic ai-title line. It carries the transcript's real sessionId
  // (so it belongs to the session being imported) and is itself a valid
  // CC-detection line (`type` + `sessionId`).
  const titleLine = JSON.stringify({
    type: "ai-title",
    sessionId,
    aiTitle: sessionName,
    timestamp: meta.started_at || new Date().toISOString(),
  });

  return { content: `${titleLine}\n${jsonlContent}`, sessionName };
}
